import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} BnplPriceBarRefs
 * @property {HTMLElement} amount
 */

/**
 * Updates the BNPL instalment amount when the selected variant changes,
 * mirroring the same-purpose logic in product-price.js. Also positions the
 * popover's arrow under the trigger label, since the popover itself is
 * anchored full-width to the block rather than to the trigger.
 *
 * The popover and trigger label live inside a nested `anchored-popover-component`,
 * so they belong to that component's own `refs` (component.js scopes `[ref]` lookups
 * to the nearest ancestor whose tag name ends in `-component`) — queried directly here instead.
 *
 * @extends {Component<BnplPriceBarRefs>}
 */
export class BnplPriceBarComponent extends Component {
  requiredRefs = ['amount'];

  /**
   * Subscribes to variant-change events (for the instalment amount) and to the
   * popover's own toggle event (for arrow positioning).
   */
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    const popover = this.querySelector('[ref="popover"]');
    if (popover) popover.addEventListener('toggle', this.#handlePopoverToggle);
  }

  /**
   * Mirrors connectedCallback's subscriptions so both listeners are torn down together.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    const popover = this.querySelector('[ref="popover"]');
    if (popover) popover.removeEventListener('toggle', this.#handlePopoverToggle);
  }

  /**
   * @param {Event} event
   */
  #handlePopoverToggle = (event) => {
    const toggleEvent = /** @type {ToggleEvent} */ (event);
    if (toggleEvent.newState !== 'open' || !(toggleEvent.currentTarget instanceof HTMLElement)) return;

    const triggerLabel = this.querySelector('[ref="triggerLabel"]');
    if (!triggerLabel) return;

    const popover = toggleEvent.currentTarget;
    const labelRect = triggerLabel.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const arrowLeft = labelRect.left + labelRect.width / 2 - popoverRect.left;

    popover.style.setProperty('--bnpl-price-bar-arrow-left', `${arrowLeft}px`);
  };

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newComponent = detail.html.querySelector(
          `bnpl-price-bar-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newComponent) return;

        const newAmount = newComponent.querySelector('[ref="amount"]');
        if (newAmount && this.refs.amount) {
          this.refs.amount.replaceWith(newAmount);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bnpl-price-bar] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bnpl-price-bar-component')) {
  customElements.define('bnpl-price-bar-component', BnplPriceBarComponent);
}
